import {
  ContentPageConfig,
  HouseContent,
  HouseContentFormData,
  HouseContentType,
  TranslateFunction,
} from './types';

export const isGuideType = (contentType: HouseContentType) => contentType === 'guide';

export const isProblemType = (contentType: HouseContentType) => contentType === 'problem';

export const isLocationType = (contentType: HouseContentType) => (
  contentType === 'activities' || contentType === 'shops' || contentType === 'restaurants'
);

export const supportsContentCategories = (contentType: HouseContentType) => (
  isGuideType(contentType) || isLocationType(contentType)
);

export const createInitialFormData = (contentType: HouseContentType): HouseContentFormData => ({
  title: '',
  description: '',
  category: '',
  location: '',
  address: '',
  latitude: '',
  longitude: '',
  komootUrl: '',
  severity: 'medium',
  status: contentType === 'problem' ? 'reported' : 'approved',
});

export const getContentPageConfig = (
  contentType: HouseContentType,
  isOwner: boolean,
  t: TranslateFunction,
): ContentPageConfig => {
  switch (contentType) {
    case 'guide':
      return {
        variant: 'guide',
        pageText: {
          layoutTitle: t('articles.layoutTitle'),
          title: t('articles.title'),
          intro: t('articles.noArticlesDescription'),
          addAction: t('articles.addArticle'),
          emptyTitle: t('articles.noArticles'),
          emptyDescription: t('articles.noArticlesDescription'),
          emptyAction: t('articles.createFirst'),
          failedFetch: t('articles.failedFetch'),
          failedDelete: t('articles.failedDelete'),
          failedSave: t('articles.failedSave'),
          deleteConfirm: t('articles.deleteConfirm'),
          editTitle: t('articles.editTitle'),
          createTitle: t('articles.createTitle'),
          formDescriptionLabel: t('articles.content'),
          formCategoryLabel: t('common.fields.category'),
          formCategoryHelper: t('common.helpers.selectOrCreateCategory'),
        },
      };
    case 'activities':
      return {
        variant: 'location',
        pageText: {
          layoutTitle: t('activities.layoutTitle'),
          title: t('activities.title'),
          intro: isOwner ? t('activities.ownerEmpty') : t('activities.guestEmpty'),
          addAction: isOwner ? t('activities.addActivity') : t('activities.suggestActivity'),
          emptyTitle: t('activities.noActivities'),
          emptyDescription: isOwner ? t('activities.ownerEmpty') : t('activities.guestEmpty'),
          emptyAction: isOwner ? t('activities.addFirst') : t('activities.suggestFirst'),
          failedFetch: t('activities.failedFetch'),
          failedDelete: t('activities.failedDelete'),
          failedSave: t('activities.failedSave'),
          deleteConfirm: t('activities.deleteConfirm'),
          editTitle: t('activities.editTitle'),
          createTitle: isOwner ? t('activities.createTitle') : t('activities.suggestTitle'),
          formDescriptionLabel: t('common.fields.description'),
          formCategoryLabel: t('common.fields.category'),
          formCategoryHelper: t('common.helpers.selectOrCreateCategory'),
          readyToUpload: t('activities.readyToUpload'),
          externalLinkLabel: t('activities.viewOnKomoot'),
        },
      };
    case 'shops':
      return {
        variant: 'location',
        pageText: {
          layoutTitle: t('shops.layoutTitle'),
          title: t('shops.title'),
          intro: t('shops.noShopsDescription'),
          addAction: t('shops.addShop'),
          emptyTitle: t('shops.noShops'),
          emptyDescription: t('shops.noShopsDescription'),
          emptyAction: t('shops.createFirst'),
          failedFetch: t('shops.failedFetch'),
          failedDelete: t('shops.failedDelete'),
          failedSave: t('shops.failedSave'),
          deleteConfirm: t('shops.deleteConfirm'),
          editTitle: t('shops.editTitle'),
          createTitle: t('shops.createTitle'),
          formDescriptionLabel: t('common.fields.description'),
          formCategoryLabel: t('common.fields.category'),
          formCategoryHelper: t('common.helpers.selectOrCreateCategory'),
          readyToUpload: t('shops.readyToUpload'),
          externalLinkLabel: t('common.actions.openLink'),
        },
      };
    case 'restaurants':
      return {
        variant: 'location',
        pageText: {
          layoutTitle: t('restaurants.layoutTitle'),
          title: t('restaurants.title'),
          intro: t('restaurants.noRestaurantsDescription'),
          addAction: t('restaurants.addRestaurant'),
          emptyTitle: t('restaurants.noRestaurants'),
          emptyDescription: t('restaurants.noRestaurantsDescription'),
          emptyAction: t('restaurants.createFirst'),
          failedFetch: t('restaurants.failedFetch'),
          failedDelete: t('restaurants.failedDelete'),
          failedSave: t('restaurants.failedSave'),
          deleteConfirm: t('restaurants.deleteConfirm'),
          editTitle: t('restaurants.editTitle'),
          createTitle: t('restaurants.createTitle'),
          formDescriptionLabel: t('common.fields.description'),
          formCategoryLabel: t('common.fields.category'),
          formCategoryHelper: t('common.helpers.selectOrCreateCategory'),
          readyToUpload: t('restaurants.readyToUpload'),
          externalLinkLabel: t('common.actions.openLink'),
        },
      };
    default:
      return {
        variant: 'problem',
        pageText: {
          layoutTitle: t('problems.layoutTitle'),
          title: t('problems.title'),
          intro: t('problems.noProblemsDescription'),
          addAction: t('problems.reportProblem'),
          emptyTitle: t('problems.noProblems'),
          emptyDescription: t('problems.noProblemsDescription'),
          emptyAction: t('problems.reportFirst'),
          failedFetch: t('problems.failedFetch'),
          failedDelete: t('problems.failedDelete'),
          failedSave: t('problems.failedSave'),
          deleteConfirm: t('problems.deleteConfirm'),
          editTitle: t('problems.editTitle'),
          createTitle: t('problems.createTitle'),
          formDescriptionLabel: t('common.fields.description'),
        },
      };
  }
};

export const canCreateContent = (contentType: HouseContentType, isOwner: boolean) => {
  if (contentType === 'guide' || contentType === 'shops' || contentType === 'restaurants') {
    return isOwner;
  }

  return true;
};

export const canEditContentItem = (
  contentType: HouseContentType,
  item: HouseContent,
  isOwner: boolean,
  currentUserId?: string,
) => {
  if (contentType === 'guide' || contentType === 'shops' || contentType === 'restaurants') {
    return isOwner;
  }

  if (contentType === 'activities') {
    return isOwner || (item.submittedBy === currentUserId && item.status === 'pending');
  }

  return isOwner || (item.submittedBy === currentUserId && item.status === 'reported');
};

export const getTypeChipLabel = (contentType: HouseContentType, t: TranslateFunction) => {
  if (contentType === 'guide') {
    return t('common.labels.guide');
  }

  if (contentType === 'activities') {
    return t('houseDetail.tabs.activities');
  }

  if (contentType === 'shops') {
    return t('houseDetail.tabs.shops');
  }

  return t('houseDetail.tabs.restaurants');
};